<template>
  <Modal
    :title="`Photos for &quot;${question.title}&quot;`"
    submit-btn-text="Done"
    @hide-modal="emit('hide-modal')"
    @submit="emit('submit', [...selectedIds])"
  >
    <div class="mb-3 flex items-center justify-between gap-3">
      <p class="cf-hint mb-0" data-cy="cf-photo-picker-hint">
        {{ hint }}
      </p>

      <SeraButton
        v-if="uploadLink"
        icon="fa-solid fa-images"
        :disabled="uploading"
      >
        <label class="mb-0 cursor-pointer">
          Upload
          <input
            ref="fileInputRef"
            type="file"
            :accept="PHOTO_ACCEPT"
            multiple
            class="hidden"
            data-cy="cf-photo-upload"
            @change="handleUpload"
          />
        </label>
      </SeraButton>
    </div>

    <p v-if="!jobPhotos.length" class="cf-hint" data-cy="cf-no-job-photos">
      This job has no photos yet. Upload one to attach it to this question.
    </p>

    <!-- Clicking a tile toggles it, so the same grid attaches and detaches and
         there is no separate "remove" affordance to keep in sync. -->
    <ul v-else class="cf-photo-grid" data-cy="cf-photo-picker-grid">
      <li v-for="photo in jobPhotos" :key="photo.id">
        <button
          type="button"
          class="cf-photo-tile"
          :class="{ 'cf-photo-tile--on': isSelected(photo.id) }"
          :aria-pressed="isSelected(photo.id)"
          :aria-label="photo.caption || `Photo ${photo.id}`"
          :data-cy="`cf-photo-option-${photo.id}`"
          @click="toggle(photo.id)"
        >
          <img
            :src="thumbnailUrl(photo)"
            :alt="photo.caption || ''"
            loading="lazy"
          />
          <span v-if="isSelected(photo.id)" class="cf-photo-check">
            <i class="fa-solid fa-check"></i>
          </span>
        </button>
      </li>
    </ul>

    <MediaUploadProgressModal
      v-if="uploading"
      media-type="photo"
      :file-count="selectedFiles.length"
      :total-file-size="totalFileSize"
      :progress="progress"
      @hide-modal="handleCancel"
    />
  </Modal>
</template>

<script setup lang="ts">
import escape from "lodash/escape";
import moment from "moment";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import {
  partitionPhotos,
  PHOTO_ACCEPT,
  unsupportedPhotoMessage
} from "@/lib/photoUploads";

import SeraButton from "@/components/Button.vue";
import MediaUpload from "@/components/MediaUpload";
import MediaUploadProgressModal from "@/components/MediaUploadProgressModal.vue";
import Modal from "@/components/Modal.vue";
import CONSTANTS from "@/Constants.js";
import type {
  CustomFormAnswer,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";
import type { FailedUpload, MediaFile } from "@/typings/media-upload";
import type { Photo } from "@/typings/photo";

// Picks which of the job's photos answer one question. The caller owns the write:
// this emits the final id list, which WriteAnswer#sync_photos applies as a
// replacement in the given order.
//
// Uploading here adds to the job's pool and preselects the result rather than
// attaching server-side. The photos#complete endpoint can attach on upload via
// custom_form_answer_id, but that path exists for the tech app capturing straight
// into a question — it has no confirm step, and an answer row to attach to. Here
// the modal's product is a final set, and the question may have no answer yet.
// No saving state: the write happens after this closes, so there is nothing to
// show progress for here.
const props = defineProps<{
  question: CustomFormSubmissionQuestion;
  answer: CustomFormAnswer | null;
}>();

const emit = defineEmits<{
  "hide-modal": [];
  submit: [photoIds: number[]];
}>();

const { MEGABYTE, PHOTO_MAX_SIZE, PHOTO_MAX_COUNT } = CONSTANTS;

const store = useStore();

const fileInputRef = ref<HTMLInputElement | null>(null);
const uploading = ref(false);
const selectedFiles = ref<MediaFile[]>([]);
const progress = ref(0);
let uploader: MediaUpload | null = null;

// Seeded from the answer and edited locally, so cancelling changes nothing.
const selectedIds = ref<number[]>(
  (props.answer?.photos ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((photo) => photo.id)
);

const jobPhotos = computed<Photo[]>(() => store.state.job?.photos ?? []);
const uploadLink = computed(() => store.state.job?.links?.reserve_s3_photos);

const hint = computed(() => {
  const min = props.question.photo_min_count;
  const selected = selectedIds.value.length;
  if (min > 0) return `${selected} selected — at least ${min} required.`;

  return `${selected} selected.`;
});

const totalFileSize = computed(() =>
  selectedFiles.value.reduce((total, file) => total + file.file.size, 0)
);

function isSelected(id: number) {
  return selectedIds.value.includes(id);
}

// Order is meaningful (sync_photos writes position from it), so a re-selected
// photo goes to the end rather than back to where it used to sit.
function toggle(id: number) {
  selectedIds.value = isSelected(id)
    ? selectedIds.value.filter((selected) => selected !== id)
    : [...selectedIds.value, id];
}

// A freshly uploaded photo's thumb may not exist yet, so fall back to the full
// image for the first couple of minutes.
function thumbnailUrl(photo: Photo) {
  if (!photo?.file) return "";
  if (moment().diff(moment(photo.created_at), "minutes") < 2) {
    return photo.file.url;
  }
  return photo.file.thumb?.url || photo.file.url;
}

function clearInput() {
  if (fileInputRef.value) fileInputRef.value.value = "";
}

function handleUpload(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  const { storable, rejected } = partitionPhotos(Array.from(target.files));

  if (rejected.length) window.notify.error(unsupportedPhotoMessage(rejected));

  // Counted after the partition, because the cap is enforced per request and
  // only the storable files reach it.
  // Cleared on both refusals, or re-picking the same corrected selection fires
  // no change event and the picker looks dead.
  if (storable.length > PHOTO_MAX_COUNT) {
    window.notify.error(
      `You can only upload a max of ${PHOTO_MAX_COUNT} photos at one time. Try splitting photos into multiple uploads.`
    );
    clearInput();
    return;
  }

  if (!storable.length) {
    clearInput();
    return;
  }

  // Caught here rather than awaited by the caller: a change handler cannot return
  // a promise, and the job refetch inside can fail on its own. The interceptor has
  // already flashed whatever went wrong; this only stops an unhandled rejection.
  uploadPhotos(
    storable.map((file) => ({
      file,
      fileName: file.name
    }))
  ).catch(() => {});
}

async function uploadPhotos(files: MediaFile[]) {
  if (!uploadLink.value) return;

  const before = new Set(jobPhotos.value.map((photo) => photo.id));
  uploading.value = true;
  selectedFiles.value = files;
  // source: custom_form marks these as captured for a checklist, which is what
  // the gallery's source filter reads.
  uploader = new MediaUpload({
    mediaType: "photo",
    link: uploadLink.value,
    files,
    uploadSource: "custom_form"
  });
  uploader.onProgress = ({ progress: value }) => {
    progress.value = value * 100;
  };

  try {
    await uploader.uploadAll();
    reportUploadOutcome();
    await refreshAndSelectNew(before);
  } finally {
    resetUploader();
    clearInput();
  }
}

// MediaUpload reports counts, not the created records, so the new photos are
// whatever the job did not have before the refetch.
async function refreshAndSelectNew(before: Set<number>) {
  if (!uploader?.success) return;

  await store.dispatch("fetchJob", { jobId: store.state.job.id });
  const added = jobPhotos.value
    .filter((photo) => !before.has(photo.id))
    .map((photo) => photo.id);
  selectedIds.value = [...selectedIds.value, ...added];
}

function reportUploadOutcome() {
  if (!uploader) return;

  const { failures, success, failedUploads } = uploader;
  if (!failures) {
    window.notify.success(`${success} photo(s) uploaded successfully!`);
    return;
  }

  const detail = failedUploads
    .map(
      (upload: FailedUpload, index: number) =>
        `${index + 1}) ${escape(upload.fileName)} (${formatFileSize(
          upload.file.size
        )}) - ${failureReason(upload)}<br/>`
    )
    .join("");
  window.notify.error(
    `${failures} out of ${success + failures} photo(s) failed to upload.<br/><br/>${detail}`
  );
}

function formatFileSize(bytes: number) {
  return `${(bytes / MEGABYTE).toFixed(2)}MB`;
}

function failureReason(upload: FailedUpload) {
  if (upload.reason === "too_large") {
    return `exceeds maximum size of ${Math.round(PHOTO_MAX_SIZE / MEGABYTE)}MB`;
  }
  return "failed to upload";
}

function handleCancel() {
  if (uploader) {
    uploader.onProgress = null;
    uploader.cancel();
    window.notify.error("Cancelled uploading photo(s)");
  }
  resetUploader();
}

function resetUploader() {
  uploading.value = false;
  selectedFiles.value = [];
  progress.value = 0;
  uploader = null;
}
</script>

<style scoped>
.cf-hint {
  color: var(--neutral-gray-700);
  font-size: 12px;
}

.cf-photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  max-height: 340px;
  overflow-y: auto;
  list-style: none;
}

.cf-photo-tile {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: 2px solid var(--neutral-gray-300);
  border-radius: 4px;
  overflow: hidden;
  background: var(--neutral-gray-100);
}

.cf-photo-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cf-photo-tile--on {
  border-color: var(--primary-blue-500, #2563eb);
}

.cf-photo-check {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: var(--primary-blue-500, #2563eb);
  color: #fff;
  font-size: 11px;
}
</style>
